import { useState } from 'react';
import { useOpenRequestsCheck } from './useOpenRequestsCheck';

/**
 * Hook that guards request submission against duplicates. When the request carries a dynamic
 * input, open requests matching the same action and input are looked up first. Any matches are
 * held for the caller to display, and the submit is deferred until the user proceeds. Everything
 * else submits straight through.
 * @param {() => void} close - Returns the user to where they came from when they cancel
 */
export const useDuplicateRequestCheck = (close) => {
  const { mutate: checkOpenRequests, isPending: isChecking } = useOpenRequestsCheck();

  const [duplicateRequests, setDuplicateRequests] = useState(/** @type {Local.OpenRequestSummary[]} */ ([]));
  // Held in an object so useState stores the callback rather than treating it as a state updater
  const [pendingSubmit, setPendingSubmit] = useState(/** @type {{ submit: () => void } | null} */ (null));

  /**
   * Check for open duplicates, then either submit the request or hold it pending confirmation.
   * @param {Pella.ProductionRequestAPI.CreateRequestInput} input - The request to submit
   * @param {(input: Pella.ProductionRequestAPI.CreateRequestInput) => void} submit - Submits the request
   */
  const submitWithCheck = (input, submit) => {
    const shouldCheckForDuplicates = Boolean(input.dynamicInput);

    if (!shouldCheckForDuplicates) {
      submit(input);
      return;
    }

    checkOpenRequests(
      { actionId: input.actionId, dynamicInput: /** @type {string} */ (input.dynamicInput) },
      {
        onSuccess: (openRequests) => {
          if (!openRequests?.length) {
            submit(input);
            return;
          }
          setPendingSubmit({ submit: () => submit(input) });
          setDuplicateRequests(openRequests);
        },
      },
    );
  };

  /** Discard the duplicates and the request that was waiting on them */
  const clearDuplicates = () => {
    setDuplicateRequests([]);
    setPendingSubmit(null);
  };

  /** Abandon the request and return the user to where they came from */
  const onCancel = () => {
    clearDuplicates();
    close();
  };

  /**
   * Submit the request the user was warned about. Closing is left to the submit callback, which
   * navigates once the mutation settles.
   */
  const onProceed = () => {
    pendingSubmit?.submit();
    clearDuplicates();
  };

  return { duplicateRequests, submitWithCheck, onCancel, onProceed, isChecking };
};
